import { Box, Flex, Text } from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import type { PercentileCurvePoint, PatientDataPoint } from './data'
import {
  interpolatePercentile,
  classifyPoint,
  type DotColor
} from './data/classify'
import messages from './messages'

interface ChartDisplayProps {
  percentiles: PercentileCurvePoint[]
  patientPoints: PatientDataPoint[]
  yLabel: string
  yMin: number
  yMax: number
  measurementLabel: string
}

const PERCENTILE_COLORS = {
  p3: '#94a3b8',
  p15: '#64748b',
  p50: '#2563eb',
  p85: '#64748b',
  p97: '#94a3b8'
}

const DOT_COLORS = {
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444'
}

interface TooltipPayloadItem {
  name: string
  value: number
  payload?: PatientDataPoint
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayloadItem[]
  label?: number
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  const { formatMessage } = useIntl()

  if (!active || !payload?.length) return null

  const patientItem = payload.find(
    (p) =>
      p.name === formatMessage(messages.normalRange) ||
      p.name === formatMessage(messages.borderlineRange) ||
      p.name === formatMessage(messages.abnormalRange)
  )

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={3}
      shadow="md"
    >
      <Text fontSize="sm">
        {formatMessage(messages.ageMonths)}: {label}
      </Text>
      {patientItem?.payload && (
        <Text fontSize="sm" fontWeight="semibold">
          {patientItem.name}: {patientItem.payload.value}
        </Text>
      )}
    </Box>
  )
}

interface ScatterPoint {
  x: number
  y: number
  visitDate: string
  month: number
  value: number
}

export default function ChartDisplay({
  percentiles,
  patientPoints,
  yLabel,
  yMin,
  yMax,
  measurementLabel
}: ChartDisplayProps) {
  const { formatMessage } = useIntl()

  if (patientPoints.length === 0) {
    return (
      <Box px={8} py={4}>
        <Box
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="md"
          p={4}
          textAlign="center"
        >
          <Text color="blue.700" fontSize="sm">
            {formatMessage(messages.noDataMessage, {
              measurement: measurementLabel
            })}
          </Text>
        </Box>
      </Box>
    )
  }

  // Classify each point by color
  const classified: Array<{ point: ScatterPoint; color: DotColor }> =
    patientPoints.map((pt) => {
      const pct = interpolatePercentile(percentiles, pt.month)
      const color = pct ? classifyPoint(pt.value, pct) : 'red'
      return {
        point: {
          x: pt.month,
          y: pt.value,
          visitDate: pt.visitDate,
          month: pt.month,
          value: pt.value
        },
        color
      }
    })

  const greenData = classified
    .filter((c) => c.color === 'green')
    .map((c) => c.point)
  const yellowData = classified
    .filter((c) => c.color === 'yellow')
    .map((c) => c.point)
  const redData = classified
    .filter((c) => c.color === 'red')
    .map((c) => c.point)

  return (
    <Box px={8} py={4}>
      {/* Summary banner */}
      <Flex
        gap={4}
        mb={4}
        p={3}
        bg="gray.50"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        flexWrap="wrap"
        align="center"
      >
        <Text fontSize="sm" fontWeight="semibold" color="gray.600" me={2}>
          {formatMessage(messages.summaryTitle)}:
        </Text>
        {greenData.length > 0 && (
          <Flex align="center" gap={1.5}>
            <Box
              w={3}
              h={3}
              borderRadius="full"
              bg={DOT_COLORS.green}
              flexShrink={0}
            />
            <Text fontSize="sm" color="gray.700">
              {greenData.length} {formatMessage(messages.normalRange)}
            </Text>
          </Flex>
        )}
        {yellowData.length > 0 && (
          <Flex align="center" gap={1.5}>
            <Box
              w={3}
              h={3}
              borderRadius="full"
              bg={DOT_COLORS.yellow}
              flexShrink={0}
            />
            <Text fontSize="sm" color="gray.700">
              {yellowData.length} {formatMessage(messages.borderlineRange)}
            </Text>
          </Flex>
        )}
        {redData.length > 0 && (
          <Flex align="center" gap={1.5}>
            <Box
              w={3}
              h={3}
              borderRadius="full"
              bg={DOT_COLORS.red}
              flexShrink={0}
            />
            <Text fontSize="sm" color="gray.700">
              {redData.length} {formatMessage(messages.abnormalRange)}
            </Text>
          </Flex>
        )}
      </Flex>

      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart margin={{ top: 8, right: 24, bottom: 24, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="month"
            type="number"
            domain={[0, 60]}
            ticks={Array.from({ length: 61 }, (_, i) => i)}
            tick={{ fontSize: 11 }}
            label={{
              value: formatMessage(messages.ageMonths),
              position: 'insideBottom',
              offset: -16
            }}
          />
          <YAxis
            domain={[yMin, yMax]}
            ticks={Array.from({ length: yMax - yMin + 1 }, (_, i) => yMin + i)}
            tick={{ fontSize: 11 }}
            label={{
              value: yLabel,
              angle: -90,
              position: 'insideLeft',
              offset: 12
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend verticalAlign="top" />

          {/* Percentile lines */}
          <Line
            data={percentiles}
            dataKey="p3"
            name={formatMessage(messages.p3)}
            stroke={PERCENTILE_COLORS.p3}
            strokeWidth={1}
            dot={false}
            activeDot={false}
            legendType="line"
          />
          <Line
            data={percentiles}
            dataKey="p15"
            name={formatMessage(messages.p15)}
            stroke={PERCENTILE_COLORS.p15}
            strokeWidth={1.5}
            dot={false}
            activeDot={false}
            legendType="line"
          />
          <Line
            data={percentiles}
            dataKey="p50"
            name={formatMessage(messages.p50)}
            stroke={PERCENTILE_COLORS.p50}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            legendType="line"
          />
          <Line
            data={percentiles}
            dataKey="p85"
            name={formatMessage(messages.p85)}
            stroke={PERCENTILE_COLORS.p85}
            strokeWidth={1.5}
            dot={false}
            activeDot={false}
            legendType="line"
          />
          <Line
            data={percentiles}
            dataKey="p97"
            name={formatMessage(messages.p97)}
            stroke={PERCENTILE_COLORS.p97}
            strokeWidth={1}
            dot={false}
            activeDot={false}
            legendType="line"
          />

          {/* Patient data — split by classification color */}
          {greenData.length > 0 && (
            <Scatter
              name={formatMessage(messages.normalRange)}
              data={greenData}
              dataKey="y"
              fill={DOT_COLORS.green}
              r={5}
              legendType="circle"
            />
          )}
          {yellowData.length > 0 && (
            <Scatter
              name={formatMessage(messages.borderlineRange)}
              data={yellowData}
              dataKey="y"
              fill={DOT_COLORS.yellow}
              r={5}
              legendType="circle"
            />
          )}
          {redData.length > 0 && (
            <Scatter
              name={formatMessage(messages.abnormalRange)}
              data={redData}
              dataKey="y"
              fill={DOT_COLORS.red}
              r={5}
              legendType="circle"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
